/**
 * Moving a snapshot between the library and the user's computer.
 *
 * Both directions live here because they have to agree on one thing: the file
 * is the snapshot JSON itself, exactly as it sits in the library folder. That
 * is what makes "Download" and "Add snapshot" the UI equivalent of copying a
 * file out of, or into, the shared snapshots directory by hand.
 */

import { buildZipBytes } from "./zipArchive";
import { normalizeFolderPath } from "./snapshotTree";

// The /snapshotEditor router parses bodies up to 64mb; a file past that would
// be rejected after the upload rather than before it.
const MAX_SNAPSHOT_BYTES = 64 * 1024 * 1024;

/**
 * A default file name for an added snapshot, from the chosen file.
 *
 * Narrowed to what the server accepts as a new name, so the dialog opens on a
 * value that can be submitted as-is; the user is still free to replace it.
 */
export function snapshotStemFromFileName(fileName) {
  return String(fileName || "")
    .replace(/\.json$/i, "")
    .replace(/[^A-Za-z0-9._-]+/g, "_")
    .replace(/^[._-]+/, "");
}

/**
 * Hand a snapshot to the browser as a `<name>.json` download.
 *
 * Indented, unlike the library's own files: a download is read and diffed by a
 * person, and this is the same two-space form the engine's own
 * `serializerModule.exportToFile` produces. Adding it back re-minifies it, so
 * the library on disk is unaffected either way.
 */
export function downloadSnapshotJson(snapshot, fileName) {
  const blob = new Blob([JSON.stringify(snapshot, null, 2)], { type: "application/json" });
  triggerDownload(blob, String(fileName || "snapshot.json"));
}

function triggerDownload(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Firefox needs the object to outlive the click; a tick is enough for both.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** Pack a selected folder and its nested snapshots as importable JSON files. */
export function buildSnapshotFolderArchive(folderPath, snapshots) {
  const selectedPath = normalizeFolderPath(folderPath);
  if (!selectedPath) throw new Error("Select a folder to download.");
  const safePath = (value) => value.split("/").every((part) =>
    part && part !== "." && part !== ".." && !/[\\\0]/.test(part)
  );
  if (!safePath(selectedPath)) throw new Error("This folder has an invalid archive path.");
  const folderName = selectedPath.split("/").pop();
  const encoder = new TextEncoder();
  const entries = snapshots.map(({ fileName, exportFolder, snapshot }) => {
    const sourceFolder = normalizeFolderPath(exportFolder);
    if (sourceFolder !== selectedPath && !sourceFolder.startsWith(`${selectedPath}/`)) {
      throw new Error(`${fileName} is outside the selected folder.`);
    }
    if (!safePath(sourceFolder) || !fileName || fileName === "." || fileName === ".." || /[/\\\0]/.test(fileName)) {
      throw new Error(`${fileName} has an invalid archive path.`);
    }
    const relativeFolder = sourceFolder.slice(selectedPath.length).replace(/^\//, "");
    return {
      path: [folderName, relativeFolder, fileName].filter(Boolean).join("/"),
      bytes: encoder.encode(JSON.stringify(snapshot, null, 2)),
    };
  });
  if (entries.length === 0) throw new Error("This folder has no snapshots to download.");
  return { fileName: `${folderName}.zip`, bytes: buildZipBytes(entries) };
}

export function downloadSnapshotFolderArchive(folderPath, snapshots) {
  const archive = buildSnapshotFolderArchive(folderPath, snapshots);
  triggerDownload(new Blob([archive.bytes], { type: "application/zip" }), archive.fileName);
  return snapshots.length;
}

/**
 * Read a chosen file into the snapshot object the create call posts.
 *
 * Rejects with a message meant for a snackbar: every failure here is something
 * the user picked the wrong file for, not a fault to log.
 */
export async function readSnapshotFromFile(file) {
  if (!file) throw new Error("No file was chosen.");
  if (file.size > MAX_SNAPSHOT_BYTES) {
    throw new Error("This file is larger than the 64MB a snapshot may be.");
  }

  let parsed;
  try {
    parsed = JSON.parse(await file.text());
  } catch (_err) {
    throw new Error("This file is not valid JSON.");
  }

  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    throw new Error("A snapshot must be a JSON object.");
  }
  return parsed;
}
